import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { AuthService } from '../services/auth.service';
import { switchMap, take, catchError } from 'rxjs/operators';
import { EMPTY, throwError } from 'rxjs';
import { CommonService } from '../../shared/services/common.service';
import { SecurityService } from '../services/security.service';
import { AppService } from '../services/app.service';

let isAuthErrorHandled = false;

export const AuthInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const common = inject(CommonService);
  const securityService = inject(SecurityService);
  const appService = inject(AppService);
  const isLoginEndpoint = req.url.includes('sign-in');
  const isCurrentUserEndpoint = req.url.includes('get-current-user');
  const isMoodleEndpoint =
    req.url.includes('dev-moodle.insightstobehavior.net') ||
    req.url.includes('stage-moodle.insightstobehavior.net');
  const isSamlConfigEndpoint = req.url.includes('samlconfiguration');
  const isSignOutEndpoint = req.url.includes('sign-out');
  let modifiedReq = req;

  if (req.body && typeof req.body === 'object' && !isSamlConfigEndpoint) {
    let isSecurityIssueDetected = false;
    const cleanedBody: Record<string, unknown> = { ...req.body };

    // Fields that should skip validation (e.g., legitimate URLs)
    const skipValidationFields = isSignOutEndpoint ? ['sloUrl', 'idpName'] : [];

    Object.entries(req.body).forEach(([key, value]) => {
      if (typeof value === 'string' && !skipValidationFields.includes(key)) {
        const validationResult = securityService.validateInput(value);
        if (!validationResult.isValid) {
          isSecurityIssueDetected = true;
          securityService.showSecurityAlert(
            validationResult.issue || 'Suspicious content',
            key
          );
          delete cleanedBody[key];
        }
      }
    });

    if (isSecurityIssueDetected) {
      modifiedReq = req.clone({ body: cleanedBody });
    }
  }

  return authService.authState$.pipe(
    take(1),
    switchMap(() => {
      modifiedReq = modifiedReq.clone({
        setHeaders: {
          Accept: 'application/json',
          ...(isMoodleEndpoint ? {} : securityService.getSecurityHeaders()),
        },
        withCredentials: true,
      });

      return next(modifiedReq).pipe(
        catchError(error => {
          if (error.status === 401) {
            // For login endpoint or OTP verification flow we should rethrow so callers can handle it
            if (isLoginEndpoint || isCurrentUserEndpoint) {
              return throwError(() => error);
            }
            if (
              localStorage.getItem('verify-user') ||
              localStorage.getItem('isOtpVerified')
            ) {
              return throwError(() => error);
            }

            // Attempt to refresh token; return the refresh observable so the outer stream can react
            return authService.getRefreshToken().pipe(
              take(1),
              switchMap(() => {
                // After refresh we simply complete this stream (caller can retry if needed)
                return EMPTY;
              }),
              catchError(() => {
                // If refresh fails, handle global auth error once and complete
                if (!isAuthErrorHandled) {
                  isAuthErrorHandled = true;

                  console.warn(
                    '401 Unauthorized - handling authentication error'
                  );

                  localStorage.removeItem('user_data');
                  localStorage.removeItem('user_role');
                  localStorage.removeItem('Selected_Org_Data');
                  localStorage.removeItem('auth_token');

                  appService.handleGlobalAuthError();

                  setTimeout(() => {
                    isAuthErrorHandled = false;
                  }, 3000);
                }

                return EMPTY;
              })
            );
          }

          if (error.status === 403) {
            common.notify(
              'error',
              'Access denied. Please check your permissions.'
            );
            return EMPTY;
          }
          return throwError(() => error);
        })
      );
    })
  );
};
